import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORY_META, currency } from "@/lib/constants";
import AIScoreCard from "@/components/AIScoreCard";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import { Building2, MapPin, Phone, Globe, Calendar, Users, DollarSign, FileText, ArrowLeft, Bookmark, RefreshCw, Zap, UserSearch, Mail, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DealRoom from "@/components/DealRoom";
import StreetView from "@/components/StreetView";
import InvestmentAnalysisCard from "@/components/InvestmentAnalysisCard";
import ComparablesCard from "@/components/ComparablesCard";
import MemoExportButtons from "@/components/MemoExportButtons";

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [rescoring, setRescoring] = useState(false);
  const [enrichingAvm, setEnrichingAvm] = useState(false);

  useEffect(() => {
    api.get(`/opportunities/${id}`).then((r) => setOpp(r.data));
  }, [id]);

  const rescore = async () => {
    setRescoring(true);
    try {
      const r = await api.post(`/ai/rescore/${id}`);
      setOpp({ ...opp, ai_score: r.data.ai_score });
      toast.success("AI re-analysis complete.");
    } catch (e) { toast.error("AI unavailable"); } finally { setRescoring(false); }
  };

  const enrichAvm = async () => {
    setEnrichingAvm(true);
    try {
      const r = await api.post(`/opportunities/${id}/enrich-avm`);
      if (r.data.enriched) {
        setOpp(r.data.opportunity);
        toast.success(`Live ATTOM valuation applied: ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(r.data.opportunity.attom_avm_value)}`);
      } else {
        toast.error(`No AVM available for this property (${r.data.reason})`);
      }
    } catch (e) {
      if (e.response?.status === 429) toast.error("Monthly ATTOM quota exhausted");
      else if (e.response?.status === 403) toast.error("Live valuation requires an active subscription");
      else toast.error(e.response?.data?.detail || "Enrichment failed");
    } finally { setEnrichingAvm(false); }
  };

  const addLead = async () => {
    try {
      await api.post("/crm/leads", {
        opportunity_id: opp.id, name: opp.title, company: opp.business_name,
        status: "new", pipeline_stage: "prospect", deal_value: opp.price,
        notes: `Sourced from ${opp.category} feed`,
      });
      toast.success("Added to CRM pipeline.");
    } catch { toast.error("Failed to add lead"); }
  };

  const [tracing, setTracing] = useState(false);
  const [sendingCard, setSendingCard] = useState(false);

  const runSkipTrace = async () => {
    setTracing(true);
    try {
      const r = await api.post("/skiptrace/lookup", {
        address: opp.address, city: opp.city, state: opp.state, zip: opp.zip,
        opportunity_id: opp.id,
      });
      setOpp({ ...opp, skip_trace: {
        owner_name: r.data.owner_name, phones: r.data.phones, emails: r.data.emails,
        vendor: r.data.vendor, mock: r.data.mock, traced_at: new Date().toISOString(),
      }});
      toast.success(r.data.mock
        ? "Mock skip trace (add SKIPTRACE_API_KEY for live lookups)"
        : `Found ${r.data.phones?.length || 0} phones · ${r.data.emails?.length || 0} emails`);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Skip tracing requires an active subscription");
      else toast.error(e.response?.data?.detail || "Skip trace failed");
    } finally { setTracing(false); }
  };

  const sendPostcard = async () => {
    if (!opp.address || !opp.city || !opp.state || !opp.zip) {
      toast.error("Property must have a full address");
      return;
    }
    if (!window.confirm(`Mail a yellow-letter postcard to the property owner at ${opp.address}? (~$1.50 per card in live mode)`)) return;
    setSendingCard(true);
    try {
      const ownerName = opp.skip_trace?.owner_name || "Property Owner";
      const r = await api.post("/postcards/send", {
        to_name: ownerName,
        to_address_line1: opp.address, to_city: opp.city, to_state: opp.state, to_zip: opp.zip,
        property_address: `${opp.address}, ${opp.city}, ${opp.state} ${opp.zip}`,
      });
      toast.success(r.data.postcard.mock
        ? "Mock postcard (add LOB_API_KEY for real mailing)"
        : `Postcard queued · delivery ~${r.data.postcard.expected_delivery_date || "5-8 business days"}`);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Postcards require an active subscription");
      else toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSendingCard(false); }
  };

  if (!opp) return <div className="text-[#7a6d5a] py-12 text-center">Loading opportunity…</div>;
  const meta = CATEGORY_META[opp.category] || {};

  return (
    <div className="space-y-5 max-w-6xl" data-testid="opp-detail-page">
      <Link to={`/app/opportunities/${opp.category}`} className="inline-flex items-center gap-2 text-[12px] text-[#b8ac96] hover:text-[#d4a574]">
        <ArrowLeft size={14} /> Back to {meta.label}
      </Link>

      {/* Hero */}
      <div className="relative rounded-lg overflow-hidden border border-[#33291f]">
        <img src={opp.image_url} alt="" className="w-full aspect-[21/9] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14100c] via-[#14100c]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded bg-[#14100c]/80 border border-[#33291f]" style={{ color: meta.color }}>
              {meta.label}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#7a6d5a] font-mono">ID · {opp.id.slice(0, 8)}</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">{opp.title}</h1>
          <div className="flex items-center gap-4 text-[13px] text-[#b8ac96]">
            <span className="flex items-center gap-1.5"><MapPin size={12} />{opp.address}, {opp.city}, {opp.state} {opp.zip}</span>
            {opp.county && <span>· {opp.county}</span>}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button onClick={addLead} data-testid="add-to-crm-btn" className="flex items-center gap-2 px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988]">
          <Bookmark size={14} /> Add to CRM
        </button>
        <button onClick={rescore} disabled={rescoring} data-testid="rescore-btn" className="flex items-center gap-2 px-4 py-2 rounded border border-[#33291f] text-[13px] hover:border-[#d4a574] hover:text-[#d4a574]">
          <RefreshCw size={14} className={rescoring ? "animate-spin" : ""} /> Re-analyze with AI
        </button>
        <button onClick={enrichAvm} disabled={enrichingAvm} data-testid="enrich-avm-btn" className="flex items-center gap-2 px-4 py-2 rounded border border-[#d4a574]/50 bg-[#d4a574]/5 text-[13px] text-[#d4a574] hover:bg-[#d4a574]/15 disabled:opacity-60">
          <Zap size={14} className={enrichingAvm ? "animate-pulse" : ""} /> {opp.attom_avm_value ? "Refresh ATTOM AVM" : "Live ATTOM Valuation"}
        </button>
        <button onClick={runSkipTrace} disabled={tracing} data-testid="skiptrace-btn" className="flex items-center gap-2 px-4 py-2 rounded border border-[#34d399]/50 bg-[#34d399]/5 text-[13px] text-[#34d399] hover:bg-[#34d399]/15 disabled:opacity-60">
          {tracing ? <Loader2 size={14} className="animate-spin" /> : <UserSearch size={14} />} Skip Trace Owner
        </button>
        <button onClick={sendPostcard} disabled={sendingCard} data-testid="postcard-btn" className="flex items-center gap-2 px-4 py-2 rounded border border-[#e8b988]/50 bg-[#e8b988]/5 text-[13px] text-[#e8b988] hover:bg-[#e8b988]/15 disabled:opacity-60">
          {sendingCard ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Mail Yellow Letter
        </button>
        <MemoExportButtons opp={opp} />
      </div>

      {opp.attom_avm_value && (
        <div className="border border-[#d4a574]/40 bg-gradient-to-r from-[#d4a574]/5 to-transparent rounded-lg p-4 flex items-center gap-4 flex-wrap" data-testid="avm-banner">
          <Zap size={20} className="text-[#d4a574]" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-[#d4a574] font-semibold">Live ATTOM Valuation</div>
            <div className="font-display text-2xl font-bold">{currency(opp.attom_avm_value)}</div>
          </div>
          {opp.attom_avm_high && opp.attom_avm_low && (
            <div className="text-[11px] font-mono text-[#b8ac96]">
              Range: {currency(opp.attom_avm_low)} — {currency(opp.attom_avm_high)}
            </div>
          )}
          <div className="text-[10px] font-mono text-[#7a6d5a]">Enriched {opp.attom_enriched_at?.slice(0, 10)}</div>
        </div>
      )}

      {opp.skip_trace && (
        <div className="border border-[#34d399]/40 rounded-lg bg-[#34d399]/5 p-5" data-testid="skiptrace-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <UserSearch size={16} className="text-[#34d399]" />
              <div>
                <div className="font-display font-semibold">{opp.skip_trace.owner_name || "Owner Contact"}</div>
                <div className="text-[10px] text-[#7a6d5a] font-mono">
                  {opp.skip_trace.vendor} · traced {opp.skip_trace.traced_at?.slice(0, 10)}
                  {opp.skip_trace.mock && <span className="text-[#c94a3a] ml-2">MOCK</span>}
                </div>
              </div>
            </div>
            <button onClick={runSkipTrace} disabled={tracing} className="text-[11px] text-[#34d399] hover:underline">Re-run</button>
          </div>
          {opp.skip_trace.phones?.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-1">Phones</div>
              <div className="flex gap-2 flex-wrap">
                {opp.skip_trace.phones.map((p, i) => (
                  <a key={i} href={`tel:${p.number}`} data-testid={`phone-${i}`} className="px-3 py-1.5 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#34d399] font-mono text-[13px]">
                    <Phone size={11} className="inline mr-1.5 text-[#34d399]" />
                    {p.number} <span className="text-[9px] text-[#7a6d5a]">{p.type}{p.confidence ? ` · ${Math.round(p.confidence * 100)}%` : ""}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
          {opp.skip_trace.emails?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-1">Emails</div>
              <div className="flex gap-2 flex-wrap">
                {opp.skip_trace.emails.map((e, i) => (
                  <a key={i} href={`mailto:${e.address}`} data-testid={`email-${i}`} className="px-3 py-1.5 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#34d399] font-mono text-[12px]">
                    <Mail size={11} className="inline mr-1.5 text-[#34d399]" />
                    {e.address} {e.confidence && <span className="text-[9px] text-[#7a6d5a]">{Math.round(e.confidence * 100)}%</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Est. Price", value: currency(opp.price), color: "#d4a574" },
              { label: "Est. Value", value: currency(opp.estimated_value) },
              { label: "Est. Equity", value: currency(opp.estimated_equity), color: "#34d399" },
              { label: "Tax Assessment", value: currency(opp.tax_assessment) },
              { label: "Building", value: opp.building_size_sqft ? `${opp.building_size_sqft.toLocaleString()} sqft` : "—" },
              { label: "Lot", value: opp.lot_size_sqft ? `${opp.lot_size_sqft.toLocaleString()} sqft` : "—" },
              { label: "Year Built", value: opp.year_built || "—" },
              { label: "Employees", value: opp.employees_est?.toLocaleString() || "—" },
            ].map((k) => (
              <div key={k.label} className="border border-[#33291f] rounded p-3 bg-[#1c1712]">
                <div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">{k.label}</div>
                <div className="font-mono text-[15px] font-semibold mt-1" style={{ color: k.color || "#f4ede2" }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Business intelligence */}
          <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Building2 size={16} className="text-[#d4a574]" /> Business Intelligence</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
              {[
                ["Business Name", opp.business_name],
                ["Industry", opp.industry],
                ["NAICS", opp.naics],
                ["Status", opp.business_status],
                ["Revenue (est.)", currency(opp.revenue_est)],
                ["Property Type", opp.property_type],
                ["Filing Type", opp.filing_type],
                ["Auction Date", opp.auction_date],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">{k}</span>
                  <span className="text-[#f4ede2] font-medium">{v || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Location map */}
          <div className="border border-[#33291f] rounded-lg overflow-hidden bg-[#1c1712]">
            <div className="p-4 border-b border-[#33291f] flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><MapPin size={16} className="text-[#d4a574]" /> Location</h3>
              <a href={`https://maps.google.com/?q=${opp.latitude},${opp.longitude}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#d4a574] hover:underline">Open in Google Maps →</a>
            </div>
            <div style={{ height: 300 }}>
              <MapContainer center={[opp.latitude, opp.longitude]} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <CircleMarker center={[opp.latitude, opp.longitude]} radius={12} pathOptions={{ color: meta.color, fillColor: meta.color, fillOpacity: 0.8 }} />
              </MapContainer>
            </div>
          </div>

          {/* Street View */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><MapPin size={16} className="text-[#d4a574]" /> Street View</h3>
            <StreetView lat={opp.latitude} lng={opp.longitude} address={opp.address} />
          </div>

          {/* Deal Room */}
          <DealRoom opportunityId={opp.id} />

          {/* Comparable Sales */}
          <ComparablesCard opp={opp} />

          {/* Photos */}
          {opp.photos?.length > 0 && (
            <div>
              <h3 className="font-display text-lg font-semibold mb-3">Photos</h3>
              <div className="grid grid-cols-3 gap-2">
                {opp.photos.map((p, i) => <img key={i} src={p} className="w-full aspect-[4/3] object-cover rounded border border-[#33291f]" alt="" />)}
              </div>
            </div>
          )}

          {/* Filings / source */}
          <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><FileText size={16} className="text-[#d4a574]" /> Public Records & Sources</h3>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between border-b border-[#33291f] pb-2"><span className="text-[#7a6d5a]">Data Source</span><span>{opp.source}</span></div>
              <div className="flex justify-between border-b border-[#33291f] pb-2"><span className="text-[#7a6d5a]">Filing Type</span><span>{opp.filing_type}</span></div>
              <div className="flex justify-between"><span className="text-[#7a6d5a]">Source URL</span><a href={opp.source_url} target="_blank" rel="noreferrer" className="text-[#d4a574] hover:underline">Public Record →</a></div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <AIScoreCard score={opp.ai_score} />
          <InvestmentAnalysisCard opp={opp} onUpdated={(ia) => setOpp({ ...opp, investment_analysis: ia })} />
          {opp.tax_amount_due && (
            <div className="border border-[#c084fc]/40 rounded-lg bg-[#241d16] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#c084fc] font-semibold mb-2">Tax Delinquency</div>
              <div className="font-mono text-3xl font-bold text-[#c084fc]">{currency(opp.tax_amount_due)}</div>
              <div className="text-[11px] text-[#7a6d5a] mt-1">Amount owed to taxing authority</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
